import { motion } from "framer-motion";
import { Zap, Lightbulb, Target, TrendingUp, BarChart3, Users } from "lucide-react";

const features = [
  {
    icon: Target,
    title: "Prioritize with Precision",
    description: "Focus resources on initiatives that drive real impact with data-driven prioritization frameworks.",
    color: "from-pink-500 to-red-500",
  },
  {
    icon: Zap,
    title: "Prevent Costly Misalignment",
    description: "Detect and address strategic drift before it derails your transformation efforts.",
    color: "from-orange-500 to-yellow-500",
  },
  {
    icon: TrendingUp,
    title: "Reduce Transformation Fatigue",
    description: "Monitor organizational capacity and prevent burnout across your transformation portfolio.",
    color: "from-yellow-500 to-lime-500",
  },
  {
    icon: Users,
    title: "Align Enterprise Momentum",
    description: "Synchronize cross-functional initiatives to create unified forward progress.",
    color: "from-lime-500 to-green-500",
  },
  {
    icon: Lightbulb,
    title: "See Risk Before It Escalates",
    description: "Early warning signals help you course-correct before issues become critical.",
    color: "from-pink-500 to-purple-500",
  },
];

const Features = () => {
  return (
    <section id="features" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-heading font-bold uppercase tracking-widest text-xs text-pink-400 mb-4 block" data-testid="features-label">
            Our Services
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white mb-6" data-testid="features-title">
            You Can't Fix What You Can't See.
          </h2>
          <div className="max-w-2xl mx-auto text-left">
            <p className="font-body text-base md:text-lg text-slate-400 leading-relaxed mb-4">
              LucentisIQ integrates initiative data, capacity signals, and adoption indicators into a unified Transformation Health Index.
            </p>
            <p className="font-body text-base md:text-lg text-slate-400 leading-relaxed mb-4">
              Most transformation programs track milestones and budgets.
            </p>
            <p className="font-body text-base md:text-lg text-slate-400 leading-relaxed mb-4">
              Few measure alignment. Fewer detect adoption fragility. Almost none predict organizational drift.
            </p>
            <p className="font-body text-base md:text-lg text-pink-400 leading-relaxed font-bold text-center">
              LucentisIQ changes that.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`group relative p-8 rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 hover:border-pink-500/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(236,72,153,0.2)] ${index === 4 ? 'lg:col-start-2' : ''}`}
              data-testid={`feature-card-${index}`}
            >
              <div
                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                data-testid={`feature-icon-${index}`}
              >
                <feature.icon className="w-7 h-7 text-black" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-white mb-4" data-testid={`feature-title-${index}`}>
                {feature.title}
              </h3>
              <p className="font-body text-base text-slate-400 leading-relaxed" data-testid={`feature-description-${index}`}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
